import { useCallback, useEffect, useState } from 'react';
import { usePaging } from 'hooks/usePaging';
import { useDebounce } from 'use-debounce';
import { adminGetActivities } from 'services/admin';
import { ParamsBaseGet } from 'types/common';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { deepDiff } from 'utils/diff';
import { useRoomContext } from 'contexts/RoomContext';
import dayjs from 'dayjs';

export type ActivityItem = {
  id: string;
  userId: number;
  submittedId?: number | null;
  submittedAssetId?: number | null;
  issueId?: number | null;
  adHocIssueId?: number | null;
  action: string;
  type: string;
  oldValue: null;
  newValue: string;
  createdAt: string;
  submitted?: {
    id: number;
    room?: {
      name: string;
      id: string;
      client?: {
        clientName: string;
        id: string;
      };
    };
  } | null;
  asset?: {
    id: number;
    name: string;
  } | null;
  issue?: {
    id: number;
    name?: string;
  } | null;
  adHocIssue?: {
    id: number;
    name?: string | null;
    description?: string | null;
    locationPathString?: string | null;
    client?: { id: string; clientName: string } | null;
  } | null;
  client?: {
    id: number;
    clientName: string;
  } | null;
  user?: {
    id: number;
    name: string;
  } | null;
  stack?: ActivityItem[];
};

// --- mock data ---
const SIZE_PAGE = 15;
export const useActivityInjection = () => {
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [searchText, setSearchText] = useState('');
  const [searchParams, setSearchParams] = useState('');
  const [searchType, setSearchType] = useState('partial-text');
  const [debouncedSearch] = useDebounce(searchText, 500);
  const [userSelect, setUserSelect] = useState<string>();
  const [fromDate, setFromDate] = useState<Date | null>();
  const [toDate, setToDate] = useState<Date | null>();
  const [sortBy, setSortBy] = useState<'timestamp' | 'user' | 'action' | 'itemType' | 'itemName'>(
    'timestamp'
  );
  const [itemTypeFilter, setItemTypeFilter] = useState<string>();
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');

  const { pagingInfo, setCurrentPage, setTotal } = usePaging({
    rowsPerPage: SIZE_PAGE
  });
  const { users } = useRoomContext();

  useEffect(() => {
    setSearchParams(debouncedSearch);
    setCurrentPage(1);
  }, [debouncedSearch, setCurrentPage]);

  // const getFilteredAndSortedData = useCallback(() => {
  //   let filtered = [...mockData];
  //
  //   if (searchParams.trim()) {
  //     const s = searchParams.toLowerCase();
  //     filtered = filtered.filter(
  //       (item) =>
  //         item.user.toLowerCase().includes(s) ||
  //         item.action.toLowerCase().includes(s) ||
  //         item.itemType.toLowerCase().includes(s) ||
  //         item.itemName.toLowerCase().includes(s)
  //     );
  //   }
  //
  //   filtered.sort((a, b) => {
  //     const valA =
  //       sortBy === 'timestamp' ? dayjs(a.timestamp).valueOf() : (a[sortBy] as string).toLowerCase();
  //     const valB =
  //       sortBy === 'timestamp' ? dayjs(b.timestamp).valueOf() : (b[sortBy] as string).toLowerCase();
  //
  //     if (valA < valB) return order === 'asc' ? -1 : 1;
  //     if (valA > valB) return order === 'asc' ? 1 : -1;
  //     return 0;
  //   });
  //
  //   return filtered;
  // }, [searchParams, sortBy, order]);

  const handdleGetData = useCallback(
    async (
      query: Partial<
        ParamsBaseGet & {
          keyword: string;
          userId: string;
          type: string;
          fromDate: string;
          toDate: string;
          searchType: string;
        }
      >
    ) => {
      const data = await adminGetActivities(query);
      setActivities(data.logs);
      setTotal(data.total);
    },
    [setTotal]
  );
  const { asyncCallback } = useAsyncCallback(handdleGetData, []);

  useEffect(() => {
    asyncCallback({
      fromDate:
        fromDate && dayjs(fromDate).isValid()
          ? dayjs(fromDate).startOf('days').toISOString()
          : undefined,
      toDate:
        toDate && dayjs(toDate).isValid() ? dayjs(toDate).startOf('days').toISOString() : undefined,
      page: pagingInfo.currentPage,
      size: SIZE_PAGE,
      keyword: searchParams,
      userId: userSelect || undefined,
      type: itemTypeFilter || undefined,
      searchType
    });
  }, [
    itemTypeFilter,
    pagingInfo.currentPage,
    searchParams,
    userSelect,
    fromDate,
    toDate,
    searchType,
    asyncCallback
  ]);

  const handleSort = useCallback(
    (field: typeof sortBy) => {
      if (sortBy === field) {
        setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortBy(field);
        setOrder('asc');
      }
    },
    [sortBy]
  );

  const handleChangePage = useCallback(
    (page: number) => {
      setCurrentPage(page);
    },
    [setCurrentPage]
  );

  // PREVIEW

  const [openModal, setOpenModal] = useState(false);

  const [selectedItem, setSelectedItem] = useState<ActivityItem | null>(null);
  const convertDiff = useCallback((item: ActivityItem) => {
    const before = JSON.parse(item.oldValue || '{}');
    const after = JSON.parse(item.newValue || '{}');
    const diff = deepDiff(before, after);
    return diff;
  }, []);
  const handleOpenModal = (item: ActivityItem) => {
    setSelectedItem(item);
    setOpenModal(true);
  };

  return {
    openModal,
    setOpenModal,
    itemTypeFilter,
    setItemTypeFilter,
    userSelect,
    setUserSelect,
    users,
    activities,
    pagingInfo,
    searchText,
    setSearchText,
    searchParams,
    sortBy,
    order,
    handleSort,
    handleChangePage,
    convertDiff,
    selectedItem,
    handleOpenModal,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    searchType,
    setSearchType
  };
};
